import classNames from 'classnames'
import React, { Children, useState } from 'react'

import Button from '../Button/Button'
import { TestableInterface } from '../interfaces/TestableInterface'
import './Modal.scss'

export interface ModalState {
  isOpen: boolean
}

export interface ModalPropsInterface extends TestableInterface {
  preventDrop?: boolean
  isOpen: boolean
  fullscreen?: boolean
  header?: string | null
  headerJSX?: JSX.Element
  className?: string
  closeOnOverlayClick?: boolean
  content?: any
  closeButtonLabel?: string
  showCloseIcon?: boolean
  index?: number
  style?: { [key: string]: string }
  onClose?: () => void
  size?: 'extra-wide' | 'wide' | 'medium' | 'small'
}

export const Modal: React.FC<ModalPropsInterface & ModalState> = ({
  automation = '',
  preventDrop = false,
  isOpen = false,
  fullscreen,
  header,
  headerJSX,
  className,
  closeOnOverlayClick,
  content,
  closeButtonLabel,
  showCloseIcon,
  index,
  style,
  onClose = () => null,
  size = 'wide',
  children,
}) => {
  const [isOpenState, setIsOpenState] = useState<boolean>(isOpen)
  const dangerousHtmlHandling = { __html: content }
  const numChildren = React.Children.count(children)

  const handleOnClose = () => {
    setIsOpenState(false)
    onClose()
  }

  const formatHeader = () => {
    if (headerJSX) {
      return headerJSX
    }
    if (header) {
      return (
        <div className="header">
          <h1>{header}</h1>
        </div>
      )
    }

    return <></>
  }

  const formatFooter = () => {
    const footer = getFooterFromChildren()

    if (!footer && closeButtonLabel) {
      return (
        <div className="footer">
          <Button
            label={closeButtonLabel}
            action={() => {
              handleOnClose()
            }}
            automation={automation + '-closebtn'}
          />
        </div>
      )
    }

    return footer
  }

  const formatContent = () => {
    if (content) {
      return (
        <div className="content" dangerouslySetInnerHTML={dangerousHtmlHandling} style={{ ...(style || {}) }}></div>
      )
    }

    if (numChildren > 0) {
      return (
        <div className="content" style={{ ...(style || {}) }}>
          {getChildrenWithoutFooter()}
        </div>
      )
    }

    return <></>
  }

  const getFooterFromChildren = () => {
    if (!children) {
      return null
    }
    const filterChildren = Array.isArray(children) ? children : [children]
    return filterChildren.filter((c) => !!c).reduce((a, e) => a || (e?.type === 'footer' ? e : null), null)
  }

  const getChildrenWithoutFooter = () => {
    if (!children) {
      return null
    }
    const filterChildren = Array.isArray(children) ? children : [children]
    return filterChildren.filter((e) => e?.type !== 'footer')
  }

  return (
    <>
      {isOpenState ? (
        <div
          className="Modal"
          onDragOver={(e) => preventDrop && e.preventDefault()}
          onDrop={(e) => preventDrop && e.preventDefault()}
        >
          <div
            className={classNames('Modal', 'overlay')}
            style={{ zIndex: 3000 + (index * 2 || 0) }}
            onClick={() => {
              if (closeOnOverlayClick) {
                handleOnClose()
              }
            }}
          ></div>
          <div
            className={classNames('Modal', 'window', size, className, { fullscreen })}
            style={{ zIndex: 3001 + (index * 2 || 0) }}
          >
            {formatHeader()}
            {formatContent()}
            {formatFooter()}
            {showCloseIcon && (
              <a
                className="Button close"
                onClick={() => {
                  handleOnClose()
                }}
              ></a>
            )}
          </div>
        </div>
      ) : (
        <div />
      )}
    </>
  )
}

export default Modal
